// =============================================
// TickerBar.jsx - 상단 스크롤 티커 바
// =============================================
// 화면 최상단에 주요 지수/코인 가격이 흘러가는 배너야.
// 주식 앱 상단에 있는 그 스크롤 바를 생각하면 돼!
//
// 표시 항목: KOSPI, KOSDAQ, BTC, ETH, USD/KRW
// 30초마다 자동 새로고침
// =============================================

import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API_URL = 'http://localhost:8000';

// CSS 애니메이션은 <style> 태그로 직접 삽입 (CSS 파일 없이)
const TICKER_STYLE = `
@keyframes ticker-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
.ticker-track {
  display: flex;
  animation: ticker-scroll 40s linear infinite;
  white-space: nowrap;
}
.ticker-track:hover {
  animation-play-state: paused;
}
`;

function TickerBar() {
  const [data, setData] = useState(null);
  const styleRef = useRef(null);

  // CSS 스타일 한번만 주입
  useEffect(() => {
    if (!styleRef.current) {
      const style = document.createElement('style');
      style.textContent = TICKER_STYLE;
      document.head.appendChild(style);
      styleRef.current = style;
    }
    return () => {
      if (styleRef.current) {
        document.head.removeChild(styleRef.current);
        styleRef.current = null;
      }
    };
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/market/indices`);
      setData(res.data);
    } catch (e) {
      // 서버 꺼져있으면 그냥 무시
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // 30초마다 갱신
    return () => clearInterval(interval);
  }, []);

  // 티커 아이템 정의
  const items = data
    ? [
        {
          label: 'KOSPI',
          price: data.kospi?.price?.toLocaleString('ko-KR', { maximumFractionDigits: 2 }),
          change: data.kospi?.change_pct,
        },
        {
          label: 'KOSDAQ',
          price: data.kosdaq?.price?.toLocaleString('ko-KR', { maximumFractionDigits: 2 }),
          change: data.kosdaq?.change_pct,
        },
        {
          label: 'USD/KRW',
          price: data.usdkrw?.price?.toLocaleString('ko-KR', { maximumFractionDigits: 2 }),
          change: data.usdkrw?.change_pct,
        },
        {
          label: 'BTC/USDT',
          price: '$' + data.btc?.price?.toLocaleString('en-US', { maximumFractionDigits: 0 }),
          change: data.btc?.change_pct,
        },
        {
          label: 'ETH/USDT',
          price: '$' + data.eth?.price?.toLocaleString('en-US', { maximumFractionDigits: 0 }),
          change: data.eth?.change_pct,
        },
      ]
    : [];

  if (!data) return null;

  // 아이템을 2배로 복제해서 끊김 없이 스크롤되게 해
  const doubled = [...items, ...items];

  return (
    <div style={{
      backgroundColor: '#0d1117',
      borderBottom: '1px solid #2d3748',
      overflow: 'hidden',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
    }}>
      <div className="ticker-track">
        {doubled.map((item, i) => (
          <TickerItem key={i} {...item} />
        ))}
      </div>
    </div>
  );
}

function TickerItem({ label, price, change }) {
  const isUp = change >= 0;
  const color = isUp ? '#48bb78' : '#fc8181';
  const arrow = isUp ? '▲' : '▼';

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '0 24px',
      fontSize: '0.82rem',
      borderRight: '1px solid #2d3748',
    }}>
      <span style={{ color: '#718096', fontWeight: 600 }}>{label}</span>
      <span style={{ color: '#e2e8f0' }}>{price}</span>
      <span style={{ color, fontSize: '0.75rem' }}>
        {arrow} {Math.abs(change)}%
      </span>
    </span>
  );
}

export default TickerBar;
